import React, { useState } from 'react';
interface RefineModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    dateOfBirth: string;
    timeOfBirth: string;
    placeOfBirth: string;
  }) => void;
}
export function RefineModelModal({
  isOpen,
  onClose,
  onSubmit
}: RefineModelModalProps) {
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [timeOfBirth, setTimeOfBirth] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      dateOfBirth,
      timeOfBirth,
      placeOfBirth
    });
  };
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-159" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto" data-id="element-160">
        <div className="relative bg-[#0A0A0A] border border-[#2B2B2B] w-full max-w-2xl max-h-[90vh] overflow-y-auto my-auto" role="dialog" aria-modal="true" aria-labelledby="refine-modal-title" onClick={e => e.stopPropagation()} data-id="element-161">
          {/* Close button */}
          <button onClick={onClose} className="absolute top-4 right-4 sm:top-6 sm:right-6 text-gray-600 hover:text-[#F5F5F5] transition-colors z-10" aria-label="Close" data-id="element-162">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-163">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-164" />
            </svg>
          </button>

          {/* Content */}
          <div className="p-6 sm:p-8 md:p-12 space-y-8 sm:space-y-10" data-id="element-165">
            {/* Title */}
            <div className="space-y-3 sm:space-y-4 pr-8" data-id="element-166">
              <h2 id="refine-modal-title" className="text-2xl sm:text-3xl md:text-4xl font-serif text-[#F5F5F5] leading-tight" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-167">
                Refine your system model
              </h2>
            </div>

            {/* Body copy */}
            <div className="space-y-4 sm:space-y-6 text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-168">
              <p data-id="element-169">
                Right now, your Mirror is building itself only from your answers
                and logs.
              </p>

              <p data-id="element-170">
                You can increase the resolution of the model by adding
                structural anchors (such as birth data).
              </p>

              <div className="space-y-2 sm:space-y-3 border-l-2 border-[#2B2B2B] pl-4 sm:pl-6" data-id="element-171">
                <p className="text-[#F5F5F5]" data-id="element-172">
                  This does not change your pattern.
                </p>
                <p data-id="element-173">
                  It increases the precision of how the pattern is interpreted.
                </p>
              </div>

              <p className="text-xs sm:text-sm text-gray-600" data-id="element-174">
                This is optional.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8" data-id="element-175">
              {/* Fields */}
              <div className="space-y-5 sm:space-y-6" data-id="element-176">
                {/* Date of birth */}
                <div className="space-y-2 sm:space-y-3" data-id="element-177">
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-[#F5F5F5]" data-id="element-178">
                    Date of birth
                  </label>
                  <input type="date" id="dateOfBirth" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-base focus:border-[#F5F5F5] focus:outline-none transition-colors" data-id="element-179" />
                </div>

                {/* Time of birth */}
                <div className="space-y-2 sm:space-y-3" data-id="element-180">
                  <label htmlFor="timeOfBirth" className="block text-sm font-medium text-[#F5F5F5]" data-id="element-181">
                    Time of birth{' '}
                    <span className="text-gray-600 font-normal" data-id="element-182">
                      (optional)
                    </span>
                  </label>
                  <input type="time" id="timeOfBirth" value={timeOfBirth} onChange={e => setTimeOfBirth(e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-base focus:border-[#F5F5F5] focus:outline-none transition-colors" data-id="element-183" />
                  <p className="text-xs text-gray-600" data-id="element-184">Approximate is fine</p>
                </div>

                {/* Place of birth */}
                <div className="space-y-2 sm:space-y-3" data-id="element-185">
                  <label htmlFor="placeOfBirth" className="block text-sm font-medium text-[#F5F5F5]" data-id="element-186">
                    Place of birth
                  </label>
                  <input type="text" id="placeOfBirth" value={placeOfBirth} onChange={e => setPlaceOfBirth(e.target.value)} required placeholder="City, Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-base placeholder-gray-600 focus:border-[#F5F5F5] focus:outline-none transition-colors" data-id="element-187" />
                </div>

                <p className="text-xs text-gray-600 pt-1 sm:pt-2" data-id="element-188">
                  Even approximate values are useful.
                </p>
              </div>

              {/* What happens */}
              <div className="pt-4 sm:pt-6 border-t border-[#2B2B2B]" data-id="element-189">
                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed" data-id="element-190">
                  The Mirror will recompute your baseline model using these
                  anchors and continue refining it using your real-world logs.
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-3 sm:space-y-4" data-id="element-191">
                <button type="submit" className="block w-full px-6 sm:px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-center text-sm font-medium tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-192">
                  Refine model
                </button>

                <div className="flex items-center justify-center" data-id="element-193">
                  <button type="button" onClick={onClose} className="text-sm text-gray-600 hover:text-gray-400 transition-colors" data-id="element-194">
                    Not now
                  </button>
                </div>
              </div>

              {/* Philosophy note */}
              <div className="pt-3 sm:pt-4" data-id="element-195">
                <p className="text-xs text-gray-600 italic text-center" data-id="element-196">
                  Structural anchors do not define who you are. They improve how
                  the system resolves patterns.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>;
}